import type { DriverRow, TagDefinition } from "@/types/driver";
import { PhoneService } from "@/utils/phoneService";

const phoneService = PhoneService();

/**
 * A driver's phone number as each source holds it, raw and unchecked; `null` where the source has none.
 */
export type DriverPhoneCandidates = {
  accountPhone: string | null;
  invitationPhone: string | null;
  tagPhone: string | null;
};

/**
 * The number the Dispatch Call button dials, in E.164: the first candidate that normalizes to a valid number.
 *
 * Sources are tried account, then invitation, then phone tag, so an unparseable account number falls through to the
 * next source rather than hiding the button. `null` when no source holds a dialable number.
 */
export function driverDialNumber({ accountPhone, invitationPhone, tagPhone }: DriverPhoneCandidates): string | null {
  for (const raw of [accountPhone, invitationPhone, tagPhone]) {
    const e164 = raw ? phoneService.transformPhoneNumber(raw, "us", "E164") : null;
    if (e164 !== null && phoneService.isValidNumber(e164)) {
      return e164;
    }
  }
  return null;
}

/**
 * The first value of the workspace's phone tag for a driver, found by the tag's slug.
 *
 * `null` when the workspace names no phone tag, no tag carries that slug, or the driver has no value for it.
 */
export function phoneTagValue(
  driver: Pick<DriverRow, "tags">,
  tags: readonly TagDefinition[],
  phoneTagSlug: string | null,
): string | null {
  const tag = phoneTagSlug === null ? undefined : tags.find(({ slug }) => slug === phoneTagSlug);
  return tag ? (driver.tags[tag.id]?.[0] ?? null) : null;
}

/**
 * An E.164 number as a dispatcher reads it: national format for a North American number, E.164 for any other.
 *
 * National format drops the country code, which reads naturally only where the dispatcher shares it.
 */
export function formatDialNumber(e164: string): string {
  return (e164.startsWith("+1") ? phoneService.transformPhoneNumber(e164, "us", "NATIONAL") : null) ?? e164;
}
